import { Box, Radio, RadioGroup, Tooltip } from "@mui/joy";
import { RestartAltIcon } from "../../../../../styles/StyledMUI";
import DarkTypography from "../../../../common/DarkTypography";
import DarkCheckbox from "../../../../common/DarkCheckbox";
import IconButtonCTA from "../../../../common/IconButtonCTA";
import CriterionComments from "./CriterionComments";
import { textTokens } from "../../../../../styles/globalStyles";
import type { Criteria } from "../../../../../api/followup/Followup.typed";
import { getBarColor, isSingleChoiceCriterion } from "../../helpers/followupCriteriaHelpers";

// Read-only options keep their visual state but drop hover and pointer affordances.
const readOnlyOptionsSx = { pointerEvents: "none", cursor: "default" } as const;

type FollowUpCriterionRowProps = {
  criteria: Criteria;
  moduleIndex: number;
  criteriaIndex: number;
  onCheckboxChange: (moduleIndex: number, criteriaIndex: number, option: string) => void;
  onRadioChange: (moduleIndex: number, criteriaIndex: number, value: string) => void;
  onResetCriteria: (moduleIndex: number, criteriaIndex: number) => void;
  onAddComment: (moduleIndex: number, criteriaIndex: number, text: string) => void;
  onUpdateComment: (
    moduleIndex: number,
    criteriaIndex: number,
    commentIndex: number,
    text: string,
  ) => void;
  onDeleteComment: (moduleIndex: number, criteriaIndex: number, commentIndex: number) => void;
  userName?: string;
  readOnly?: boolean;
};

export default function FollowUpCriterionRow({
  criteria,
  moduleIndex,
  criteriaIndex,
  onCheckboxChange,
  onRadioChange,
  onResetCriteria,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  userName,
  readOnly = false,
}: FollowUpCriterionRowProps) {
  const selectedOptions = criteria.status ?? [];
  const isSingleChoice = isSingleChoiceCriterion(criteria);
  const barColor = getBarColor(criteria);

  return (
    <Box key={criteriaIndex} mb={3} sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box
        sx={{
          width: "5px",
          height: "100px",
          backgroundColor: barColor,
          marginRight: barColor === "none" ? "0px" : "20px",
          display: barColor === "none" ? "none" : "block",
        }}
      />

      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <DarkTypography
            level="h4"
            colorVariant="onLight"
            sx={{
              fontSize: { xs: "0.8rem", sm: "1.2rem" },
            }}
          >
            {criteria.description}
          </DarkTypography>

          {!readOnly && selectedOptions.length > 0 && (
            <Tooltip title="Réinitialiser">
              <IconButtonCTA
                size="sm"
                ctaVariant="neutral"
                aria-label="Réinitialiser critère"
                onClick={() => onResetCriteria(moduleIndex, criteriaIndex)}
                sx={{ ml: 1 }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButtonCTA>
            </Tooltip>
          )}
        </Box>

        {isSingleChoice ? (
          <RadioGroup
            value={selectedOptions[0] ?? ""}
            sx={readOnly ? readOnlyOptionsSx : undefined}
            onChange={(e) => {
              if (!readOnly) onRadioChange(moduleIndex, criteriaIndex, e.target.value);
            }}
          >
            {criteria.options.map((option: string, optionIndex: number) => (
              <Radio
                key={optionIndex}
                value={option}
                label={option}
                readOnly={readOnly}
                sx={{ color: textTokens.color.inverse, fontSize: "14px" }}
              />
            ))}
          </RadioGroup>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              ...(readOnly ? readOnlyOptionsSx : {}),
            }}
          >
            {criteria.options.map((option: string, optionIndex: number) => (
              <Box key={optionIndex} sx={{ display: "flex", alignItems: "center" }}>
                <DarkCheckbox
                  checked={selectedOptions.includes(option)}
                  readOnly={readOnly}
                  onChange={() => {
                    if (!readOnly) onCheckboxChange(moduleIndex, criteriaIndex, option);
                  }}
                />
                <DarkTypography fontSize="14px" colorVariant="onLight" sx={{ marginLeft: 1 }}>
                  {option}
                </DarkTypography>
              </Box>
            ))}
          </Box>
        )}

        {criteria.comments && (
          <CriterionComments
            comments={criteria.comments_array ?? []}
            readOnly={readOnly}
            currentUserName={userName}
            onAdd={(text) => onAddComment(moduleIndex, criteriaIndex, text)}
            onUpdate={(commentIndex, text) =>
              onUpdateComment(moduleIndex, criteriaIndex, commentIndex, text)
            }
            onDelete={(commentIndex) => onDeleteComment(moduleIndex, criteriaIndex, commentIndex)}
          />
        )}
      </Box>
    </Box>
  );
}
